import React, { useState } from 'react';
import { IioTable } from '../../components/IioTable';
import { useProject } from '../../hooks/useProject';
import { useReports } from '../../hooks/useReports';
import { formatBytes } from '../../utilities/format';
import { PaginationControls } from '../../components/PaginationControls';
import { FilterInput } from '../../components/FilterInput';

interface Props {
  projectId: string;
}

export function ReportsTab({ projectId }: Props) {
  const { data: project } = useProject(projectId);
  const [currentPage, setCurrentPage] = useState(0);
  const [nameFilter, setNameFilter] = useState('');

  const { data: reportsPage, isPending, isFetching } = useReports(projectId, currentPage);

  const totalReports = reportsPage?.totalReports ?? 0;
  const totalPages = reportsPage?.totalPages ?? 0;
  const reports = (reportsPage?.reports ?? []).filter(r =>
    r.name.toLowerCase().includes(nameFilter.toLowerCase())
  );

  return (
    <>
      <h2>
        <i className="fas fa-file" aria-hidden="true" />{' '}
        {project?.name} Reports
      </h2>

      {totalReports > 0 && (
        <div className="clearfix">
          <FilterInput value={nameFilter} onChange={setNameFilter} />
          <p className="lead pull-right">
            {totalReports.toLocaleString()} Reports
          </p>
        </div>
      )}

      {totalPages > 1 && (
        <PaginationControls currentPage={currentPage} totalPages={totalPages} onGoTo={setCurrentPage} />
      )}

      {isPending ? (
        <div className="text-center text-muted" style={{ padding: 40 }}>
          <i className="fas fa-spinner fa-spin fa-2x" aria-hidden="true" />
          <p style={{ marginTop: 10 }}>Loading reports…</p>
        </div>
      ) : totalReports > 0 ? (
        <>
          <IioTable condensed style={{ opacity: isFetching ? 0.5 : 1, transition: 'opacity 0.15s' }}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Format</th>
                <th>Size</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {reports.map(report => (
                <tr key={report.id}>
                  <td>{report.name}</td>
                  <td>{report.type}</td>
                  <td>{report.format}</td>
                  <td>{formatBytes(report.bytes)}</td>
                  <td>
                    <span className={`label ${report.status === 'Available' ? 'label-success' : 'label-default'}`}>
                      {report.status}
                    </span>
                    {report.status === 'Available' && report.directDownloadPath && (
                      <>
                        {' '}
                        <a
                          className="btn btn-xs btn-success"
                          href={report.directDownloadPath}
                          title="Download"
                          download
                        >
                          <i className="fas fa-download" aria-hidden="true" />
                        </a>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </IioTable>

          {totalPages > 1 && (
            <PaginationControls currentPage={currentPage} totalPages={totalPages} onGoTo={setCurrentPage} />
          )}
        </>
      ) : (
        <div className="alert alert-block">
          <h4>No Reports found</h4>
        </div>
      )}
    </>
  );
}
